import CardClass from "../../../CardClass";
import PropTypes from "prop-types";
import { withRouter } from "react-router-dom";
import MUITealColor from "@mui/material/colors/teal";
import MUIGreyColor from "@mui/material/colors/grey";
import MUIStack from "@mui/material/Stack";
import MUIMenuItem from '@mui/material/MenuItem';
import MUIListItemIcon from '@mui/material/ListItemIcon';
import MUIDownloadIcon from "@mui/icons-material/Download";
import MUICloseIcon from '@mui/icons-material/Close';
import MUIReplayIcon from "@mui/icons-material/Replay";
import MUISwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ChipOrderActivityType from "../../../Chip/ChipOrderActivityType";
import QueueRoute from "../../../../Utility/Crud/QueueRoute";
import OrderStatusReason from "../../../../Utility/Crud/OrderStatusReason";
import OrderActivityType from "../../../../Utility/Crud/OrderActivityType";
import OrderStatus from "../../../../Utility/Crud/OrderStatus";
import Menu from "../../../Menu";
import Queue from "../../../../Utility/Crud/Queue";
import Team from "../../../../Utility/Crud/Team";
import * as MUIColors from "@mui/material/colors";
import Setting from "../../../../Utility/Setting";
import MainContext from "../../../../Context/MainContext/MainContext";

export default withRouter(class CardAdminQueueRouteDiagram extends CardClass {
    static contextType = MainContext; // For legacy class components

    /**
     * Constructs the CardAdminQueueRouteDiagram component.
     * @param {object} props - React props.
     */
    constructor(props) {
        super(props);
        // Restore viewType from Setting, fallback to default
        const viewMode = Setting.get("admin.queueRoutes.routingTree.viewMode");
        const initialViewType = (viewMode === "queue" || viewMode === "orderStatusReason") ? viewMode : "orderStatusReason";
        this.state = {
            orderActivityType: null,
            treeRenderDetails: {
                dom: null,
                dimensions: {
                    height: 0,
                    width: 0,
                }
            },
            expandedPreviouslySeenNodeUuids: [],
            viewType: initialViewType,
        };
    }

    /**
     * React lifecycle method. Restores orderActivityType and tree details from
     * settings if available.
     */
    componentDidMount() {
        // Restore orderActivityType if available and valid
        const orderTypeId = Setting.get("admin.queueRoutes.routingTree.orderType");
        let orderActivityType = null;
        if (
            orderTypeId &&
            this.context.orderActivityTypesIndexed &&
            this.context.orderActivityTypesIndexed[orderTypeId]
        ) {
            orderActivityType = this.context.orderActivityTypesIndexed[orderTypeId];
        }
        if (orderActivityType) {
            let tree = this.getTree(orderActivityType);
            this.setTreeNodeSizes(tree);
            let treeRenderDetails = this.getTreeRenderDetails(tree);
            this.setState({
                orderActivityType,
                treeRenderDetails
            });
        }
    }

    /**
     * Renders the main content of the card, including the selector and tree
     * diagram.
     * @returns {JSX.Element}
     */
    renderContent() {
        if (this.state.orderActivityType) {
            return (
                <>
                    {this.renderOrderActivityTypeSelector()}
                    <div style={{ height: this.state.treeRenderDetails.dimensions.height + 8, overflowX: "scroll", position: "relative" }}>
                        {this.state.treeRenderDetails.dom}
                    </div>
                </>
            );
        }

        return (
            <>
                {this.renderOrderActivityTypeSelector()}
            </>
        );
    }

    /**
     * Renders the Order Activity Type selector chips.
     * @returns {JSX.Element}
     */
    renderOrderActivityTypeSelector() {
        const handleClick = (orderActivityType) => {
            Setting.set("admin.queueRoutes.routingTree.orderType", orderActivityType.id);
            let tree = this.getTree(orderActivityType);
            this.setTreeNodeSizes(tree);

            let treeRenderDetails = this.getTreeRenderDetails(tree);

            this.setState({
                orderActivityType: orderActivityType,
                treeRenderDetails: treeRenderDetails
            });
        };

        const handleDelete = () => {
            Setting.clear("admin.queueRoutes.routingTree.orderType");
            this.setState({
                orderActivityType: null
            });
        };

        return (
            <MUIStack direction="row" spacing={1} flexWrap="wrap" useFlexGap={true} sx={{ marginBottom: 2 }}>
                {Object.values(this.context.orderActivityTypesIndexed).map((orderActivityType) => {
                    return (
                        <ChipOrderActivityType
                            key={orderActivityType.id}
                            orderActivityType={orderActivityType}
                            onClick={() => handleClick(orderActivityType)}
                            onDelete={this.state.orderActivityType && this.state.orderActivityType.id === orderActivityType.id ? handleDelete : null}
                            deleteIcon={<MUICloseIcon />}
                        />
                    );
                })}
            </MUIStack>
        );
    }

    /**
     * Renders the routing tree diagram as SVG.
     * @param {object} tree - The tree structure to render.
     * @returns {{dom: JSX.Element, dimensions: {height: number, width:
     * number}}}
     */
    getTreeRenderDetails(tree) {
        const handleClick = (queueRoute) => {
            this.props.history.push({
                pathname: "/admin/queueRoute/" + queueRoute.id
            });
        };

        // Add handler for clicking the "Previously Seen" icon
        const handlePreviouslySeenClick = (nodeUuid) => {
            if (!this.state.expandedPreviouslySeenNodeUuids.includes(nodeUuid)) {
                this.setState(prevState => ({
                    expandedPreviouslySeenNodeUuids: [...prevState.expandedPreviouslySeenNodeUuids, nodeUuid]
                }), () => {
                    // Re-render tree with expanded node
                    let tree = this.getTree(this.state.orderActivityType);
                    this.setTreeNodeSizes(tree);
                    let treeRenderDetails = this.getTreeRenderDetails(tree);
                    this.setState({ treeRenderDetails });
                });
            }
        };

        let dom = [];
        let nodeStyles = []; // Collect per-node styles

        // Width and height of each node.
        const nodeWidth = 180;
        const nodeHeight = 40;
        const nodeIconWidth = 26;
        const nodeIconHeight = nodeHeight;

        // Spacing between nodes.
        const gutter_x = 40;
        const gutter_y = 15;

        let x = 0;
        let y = 0;

        // Track the overall tree dimensions to help size the container.
        const dimensions = {
            "height": 0,
            "width": 0,
        };

        const queue = [tree];

        const renderQueueRouteIcon = (current) => {
            const nodeParts = [];

            if (current.terminationReason === "noChildren") {
                nodeParts.push(
                    <rect
                        key={current.uuid + "-iconRect"}
                        height={nodeIconHeight}
                        width={nodeIconWidth}
                        rx="4"
                        ry="4"
                        style={{ fill: "rgba(0, 0, 0, 0.1)" }}
                    >
                        <title>No children</title>
                    </rect>
                );
                nodeParts.push(
                    <path
                        key={current.uuid + "-icon"}
                        transform="translate(1, 8)"
                        fill="#fff"
                        d="M18,18H6V6H18V18Z"
                    />
                );
            } else if (current.terminationReason === "previouslySeen") {
                // Make the whole group clickable, not just the rect
                nodeParts.push(
                    <g
                        key={current.uuid + "-iconClickableGroup"}
                        style={{ cursor: "pointer" }}
                        onClick={(e) => {
                            e.stopPropagation();
                            handlePreviouslySeenClick(current.uuid);
                        }}
                    >
                        <rect
                            key={current.uuid + "-iconRect"}
                            height={nodeIconHeight}
                            width={nodeIconWidth}
                            rx="4"
                            ry="4"
                            style={{ fill: "rgba(0, 0, 0, 0.1)" }}
                        >
                            <title>Node previously expanded (click to expand)</title>
                        </rect>
                        <path
                            key={current.uuid + "-icon"}
                            transform="translate(1, 8)"
                            fill="#fff"
                            d="M12,18A6,6 0 0,1 6,12C6,11 6.25,10.03 6.7,9.2L5.24,7.74C4.46,8.97 4,10.43 4,12A8,8 0 0,0 12,20V23L16,19L12,15M12,4V1L8,5L12,9V6A6,6 0 0,1 18,12C18,13 17.75,13.97 17.3,14.8L18.76,16.26C19.54,15.03 20,13.57 20,12A8,8 0 0,0 12,4Z"
                        />
                    </g>
                );
            } else if (current.terminationReason === "routeBackToSender") {
                nodeParts.push(
                    <rect
                        key={current.uuid + "-iconRect"}
                        height={nodeIconHeight}
                        width={nodeIconWidth}
                        rx="4"
                        ry="4"
                        style={{ fill: "rgba(0, 0, 0, 0.1)" }}
                    >
                        <title>Return to Sender</title>
                    </rect>
                );
                nodeParts.push(
                    <path
                        key={current.uuid + "-icon"}
                        transform="translate(1, 8)"
                        fill="#fff"
                        d="M13.5,7A6.5,6.5 0 0,1 20,13.5A6.5,6.5 0 0,1 13.5,20H10V18H13.5C16,18 18,16 18,13.5C18,11 16,9 13.5,9H7.83L10.91,12.09L9.5,13.5L4,8L9.5,2.5L10.92,3.91L7.83,7H13.5M6,18H8V20H6V18Z"
                    />
                );
            }

            return nodeParts;
        };

        const renderQueueRoute = (current) => {
            const nodeParts = [];

            const toOrderStatusReason = this.context.orderStatusReasonsIndexed[current.self.toOrderStatusReasonId];

            let line1;
            let line2 = '';

            if (this.state.viewType === "queue") {
                // Show queue(s) for this node
                const queues = Queue.getMatchingQueuesForQueueRoute(
                    Object.values(this.context.queuesIndexed),
                    Object.values(this.context.teamQueuesIndexed),
                    Object.values(this.context.orderActivityTypesIndexed),
                    current.self
                );
                if (queues.length === 0) {
                    // Fallback to route view, add "(No Queue)" to line2
                    if (current.terminationReason === "routeBackToSender") {
                        line1 = "Return to Sender";
                        line2 = "(No Queue)";
                    } else if (toOrderStatusReason) {
                        line1 = OrderStatusReason.getDisplayName(toOrderStatusReason);
                        const toOrderStatus = this.context.orderStatusesIndexed[toOrderStatusReason.orderStatus];
                        line2 = (toOrderStatus ? OrderStatus.getDisplayName(toOrderStatus) : "") + " (No Queue)";
                    } else {
                        line1 = current.self.name;
                        line2 = "(No Queue)";
                    }
                } else {
                    // Show queue name and team(s)
                    line1 = queues.map(q => Queue.getDisplayName(q)).join(", ");
                    // Find team(s) for each queue
                    const teamQueues = Object.values(this.context.teamQueuesIndexed);
                    line2 = queues.map(queue => {
                        // Find all teamQueues for this queue
                        const teamsForQueue = teamQueues.filter(teamQueue => teamQueue.queueId === queue.id);
                        // Use Team.getDisplayName for each team
                        return teamsForQueue.map(teamQueue => {
                            return Team.getDisplayName(this.context.teamsIndexed[teamQueue.teamId]);
                        }).filter(Boolean).join(", ");
                    }).filter(Boolean).join(", ");
                }
            } else {
                // Default: show order status reason
                if (current.terminationReason === "routeBackToSender") {
                    line1 = "Return to Sender";
                    line2 = "";
                } else if (toOrderStatusReason) {
                    line1 = OrderStatusReason.getDisplayName(toOrderStatusReason);
                    // Set line2 to order status display name
                    const toOrderStatus = this.context.orderStatusesIndexed[toOrderStatusReason.orderStatus];
                    line2 = toOrderStatus ? OrderStatus.getDisplayName(toOrderStatus) : "";
                } else {
                    line1 = current.self.name;
                    line2 = "";
                }
            }

            // Main rectangle
            nodeParts.push(
                <rect
                    key={current.uuid + "-mainRect"}
                    className={current.self.id !== 0 ? `node node-${current.uuid}` : `node_initial node-${current.uuid}`}
                    height={nodeHeight}
                    width={nodeWidth}
                    rx="4"
                    ry="4"
                    onClick={() => current.self.id !== 0 && handleClick(current.self)}
                >
                    <title>{QueueRoute.getDisplayName(current.self)}</title>
                </rect>
            );

            // Icons
            const hasIcon = current.terminationReason !== undefined;
            if (hasIcon === true) {
                nodeParts.push(
                    <g key={current.uuid + "-iconGroup"} transform={`translate(${nodeWidth - nodeIconWidth}, 0)`}>
                        {renderQueueRouteIcon(current)}
                    </g>
                );
            }

            const fontFamily = "Arial, Helvetica, sans-serif";
            const fontSize = "11px";

            // Text
            const availableWidth = nodeWidth - (hasIcon ? nodeIconWidth : 0) - 13;

            if (this.getTextWidth(line1, fontFamily, fontSize) > availableWidth) {
                while (this.getTextWidth(line1, fontFamily, fontSize) > availableWidth) {
                    line1 = line1.slice(0, -2).trimEnd();
                }
                line1 += "...";
            }

            if (this.getTextWidth(line2, fontFamily, fontSize) > availableWidth) {
                while (this.getTextWidth(line2, fontFamily, fontSize) > availableWidth) {
                    line2 = line2.slice(0, -2).trimEnd();
                }
                line2 += "...";
            }

            nodeParts.push(
                <text key={current.uuid + "-textLine1"} x="6" y="16" fontFamily={fontFamily} fontSize={fontSize} fill="#fff">
                    {line1}
                </text>
            );

            nodeParts.push(
                <text key={current.uuid + "-textLine2"} x="6" y="32" fontFamily={fontFamily} fontSize={fontSize} fill="#fff">
                    {line2}
                </text>
            );

            // Lines
            const stroke = MUIGreyColor[500];
            let x1, y1, x2, y2;
            if (previous) {
                // Left horiontal line
                x1 = 0;
                y1 = nodeHeight / 2;
                x2 = -1 * (gutter_x / 2);
                y2 = y1;
                nodeParts.push(
                    <line key={current.uuid + "-leftHorizontalLine"} x1={x1} y1={y1} x2={x2} y2={y2} stroke={stroke} />
                );

                // Arrow
                nodeParts.push(
                    <polygon key={current.uuid + "-arrow"} points={`0,${nodeHeight / 2} -4,${(nodeHeight / 2) + 4} -4,${(nodeHeight / 2) - 4}`} fill={stroke} />
                );
            }

            if (previous && current.parent === previous.parent) {
                // Left vertical line
                x1 = -1 * (gutter_x / 2);
                y1 = nodeHeight / 2;
                x2 = x1;
                y2 = (-1 * (current.y - previous.y)) + y1;
                nodeParts.push(
                    <line key={current.uuid + "-leftVerticalLine"} x1={x1} y1={y1} x2={x2} y2={y2} stroke={stroke} />
                );

            }

            if (current.children.length > 0) {
                // Right horiontal line
                x1 = nodeWidth;
                y1 = nodeHeight / 2;
                x2 = nodeWidth + (gutter_x / 2);
                y2 = y1;
                nodeParts.push(
                    <line key={current.uuid + "-rightHorizontalLine"} x1={x1} y1={y1} x2={x2} y2={y2} stroke={stroke} />
                );
            }

            return nodeParts;
        };

        let previous;
        while (queue.length > 0) {
            const current = queue.shift();

            x = (current.depth * (nodeWidth + gutter_x));

            // Set y of current to y of the parent when the parent changes.
            if (!previous || current.parent !== previous.parent) {
                y = current.parent ? current.parent.y : 0;
            }

            current.x = x;
            current.y = y;

            dimensions.height = Math.max(dimensions.height, (y + nodeHeight + gutter_y));
            dimensions.width = Math.max(dimensions.width, (x + nodeWidth + gutter_x));

            let fillColor = MUITealColor[500];
            let fillHoverColor = MUITealColor[300];

            // If viewType is "queue", use department color for all nodes
            if (this.state.viewType === "queue") {
                // Find queues for this node
                const queues = Queue.getMatchingQueuesForQueueRoute(
                    Object.values(this.context.queuesIndexed),
                    Object.values(this.context.teamQueuesIndexed),
                    Object.values(this.context.orderActivityTypesIndexed),
                    current.self
                );
                // Find team(s) for each queue
                let teamColor = null;
                if (queues.length > 0) {
                    const teamQueues = Object.values(this.context.teamQueuesIndexed);
                    // Find first team for the first queue
                    for (const queue of queues) {
                        const teamsForQueue = teamQueues.filter(teamQueue => teamQueue.queueId === queue.id);
                        if (teamsForQueue.length > 0) {
                            // Use first team
                            const team = this.context.teamsIndexed[teamsForQueue[0].teamId];
                            if (team && team.departmentId) {
                                const department = this.context.departmentsIndexed[team.departmentId];
                                if (department && department.colorId) {
                                    const color = this.context.colorsIndexed[department.colorId];
                                    if (color && MUIColors[color.hue] && MUIColors[color.hue][color.shade]) {
                                        teamColor = MUIColors[color.hue][color.shade];
                                        fillHoverColor = MUIColors[color.hue][color.shade - 200] || teamColor;
                                        break;
                                    }
                                }
                            }
                        }
                    }
                }
                fillColor = teamColor || MUITealColor[500];
            } else {
                if (current.self.toOrderStatusReasonId) {
                    const toOrderStatusReason = this.context.orderStatusReasonsIndexed[current.self.toOrderStatusReasonId];
                    const toOrderStatus = this.context.orderStatusesIndexed[toOrderStatusReason.orderStatus];
                    let color = this.context.colorsIndexed[toOrderStatus.colorId];

                    fillColor = MUIColors[color.hue][color.shade];
                    fillHoverColor = MUIColors[color.hue][color.shade - 200];
                } else {
                    fillColor = MUIGreyColor[500];
                    fillHoverColor = MUIGreyColor[300];
                }
            }

            // Collect per-node style
            nodeStyles.push(`
                .node-${current.uuid} {
                    fill: ${current.self.id !== 0 ? fillColor : OrderActivityType.getColor()};
                    cursor: pointer;
                    transition: fill 200ms ease;
                }
                .node-${current.uuid}:hover {
                    fill: ${fillHoverColor};
                }
            `);

            dom.push(
                <g key={current.uuid + "-mainGroup"} transform={`translate(${x}, ${y})`}>
                    {renderQueueRoute(current)}
                </g>
            );

            y += (current.size * (nodeHeight + gutter_y));

            for (const node of current.children) {
                queue.push(node);
            }

            previous = current;
        }

        // Inject a single <style> block at the top of the SVG
        dom.unshift(
            <style key="nodeStyles">{`
                text { pointer-events: none; }
                .node_initial { fill: ${OrderActivityType.getColor()}; }
                ${nodeStyles.join("\n")}
            `}</style>
        );

        return {
            dom: (<svg id="routingTree" xmlns="http://www.w3.org/2000/svg" width={dimensions.width} height={dimensions.height}>{dom}</svg>),
            dimensions: dimensions
        };
    }

    /**
     * Calculates the width of a text string for SVG rendering.
     * @param {string} textContent - Text to measure.
     * @param {string} fontFamily - Font family.
     * @param {string} fontSize - Font size.
     * @returns {number} Width of the text in pixels.
     */
    getTextWidth(textContent, fontFamily, fontSize) {
        const svgNode = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        svgNode.setAttribute("style", "position: absolute; top: -1000; left: -1000");

        const textNode = document.createElementNS("http://www.w3.org/2000/svg", "text");
        textNode.setAttribute("font-family", fontFamily);
        textNode.setAttribute("font-size", fontSize);
        textNode.textContent = textContent;

        svgNode.appendChild(textNode);
        document.body.appendChild(svgNode);
        const boundingBox = textNode.getBBox();
        document.body.removeChild(svgNode);

        return boundingBox.width;
    }

    /**
     * Generates a stable node UUID based on its path in the tree.
     * @param {Array<number>} pathArray - Array of route IDs from root to this
     * node.
     * @returns {string} UUID string.
     */
    getNodeUuid(pathArray) {
        return pathArray.join("-");
    }

    /**
     * Builds a tree structure of queue routes for the given order activity
     * type. Prevents loops by marking previously seen nodes.
     * @param {object} orderActivityType - The order activity type.
     * @returns {object} Tree root node.
     */
    getTree(orderActivityType) {
        const seenOrderStatusReasonIds = [];
        const seenNodeUuids = [];

        // Making up a fake route for the first node in the tree.
        const rootQueueRoute = {
            id: 0,
            orderActivityTypeId: orderActivityType.id,
            fromOrderStatusReasonId: null,
            toOrderStatusReasonId: null,
            name: OrderActivityType.getDisplayName(orderActivityType) + " - Initial"
        };

        // Use path array to generate stable UUIDs
        const rootNode = {
            uuid: this.getNodeUuid([rootQueueRoute.id]),
            self: rootQueueRoute,
            children: [],
            path: [rootQueueRoute.id]
        };
        const queue = [rootNode];

        while (queue.length > 0) {
            const current = queue.shift();

            const actualChildren = this.getChildren(current.self);
            let usedChildren = [];
            // Prevent expansion if trigger === "routeBackToSender"
            if (current.self.trigger === "routeBackToSender") {
                current.terminationReason = "routeBackToSender";
            } else if (actualChildren.length === 0) {
                current.terminationReason = "noChildren";
            } else {
                // If this route has been seen before and is not in the expanded exception list, terminate.
                if (
                    seenOrderStatusReasonIds.includes(current.self.toOrderStatusReasonId) === true &&
                    current.self.id !== 0 &&
                    !this.state.expandedPreviouslySeenNodeUuids.includes(current.uuid)
                ) {
                    current.terminationReason = "previouslySeen";
                } else {
                    usedChildren = actualChildren;
                }
            }

            seenOrderStatusReasonIds.push(current.self.toOrderStatusReasonId);
            seenNodeUuids.push(current.uuid);

            for (const child of usedChildren) {
                const childPath = [...current.path, child.id];
                const childNode = {
                    uuid: this.getNodeUuid(childPath),
                    self: child,
                    parent: current,
                    children: [],
                    path: childPath
                };
                current.children.push(childNode);
                queue.push(childNode);
            }
        }

        return rootNode;
    }

    /**
     * Gets the children queue routes for a given route.
     * @param {object} fromQueueRoute - The parent queue route.
     * @returns {Array<object>} Array of child queue routes.
     */
    getChildren(fromQueueRoute) {
        return QueueRoute.sort(
            Object.values(this.context.queueRoutesIndexed).filter((queueRoute) => {
                return (
                    queueRoute.orderActivityTypeId === fromQueueRoute.orderActivityTypeId &&
                    queueRoute.fromOrderStatusReasonId === fromQueueRoute.toOrderStatusReasonId
                );
            })
        );
    }

    /**
     * Recursively calculates the size and depth of each subtree node.
     * @param {object} tree - The tree node.
     * @param {number} [depth=0] - Current depth.
     * @returns {number} Size of the subtree.
     */
    setTreeNodeSizes(tree, depth = 0) {
        let size = 0;
        tree.children.forEach((node) => {
            size += this.setTreeNodeSizes(node, depth + 1);
        });
        tree.size = Math.max(1, size);
        tree.depth = depth;

        return tree.size;
    }

    /**
     * @returns {string} The title of the card.
     */
    getTitle() {
        return "Routing Tree";
    }

    /**
     * Returns the actions available for the card (download, reset, toggle
     * view).
     * @returns {Array<JSX.Element>}
     */
    getActions() {
        /**
         * Downloads the current tree diagram as a PNG image.
         */
        const handleDownload = () => {
            const svgBlob = new Blob([document.getElementById("routingTree").outerHTML], { type: 'image/svg+xml;charset=utf-8' });
            const url = URL.createObjectURL(svgBlob);

            const image = new Image();
            image.onload = () => {
                const canvas = document.createElement("canvas");
                canvas.width = this.state.treeRenderDetails.dimensions.width;
                canvas.height = this.state.treeRenderDetails.dimensions.height;
                const context = canvas.getContext("2d");

                context.drawImage(
                    image,
                    0,
                    0,
                    this.state.treeRenderDetails.dimensions.width,
                    this.state.treeRenderDetails.dimensions.height
                );
                URL.revokeObjectURL(url);

                canvas.toBlob((blob) => {
                    const a = document.createElement("a");
                    a.href = URL.createObjectURL(blob);
                    a.download = `Queue Routes - ${OrderActivityType.getDisplayName(this.state.orderActivityType)} - ${new Date().toISOString().slice(0, 10)}.png`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                }, "image/png");
            };

            image.src = url;
        };

        /**
         * Resets the expanded nodes view in the tree diagram.
         */
        const handleResetView = () => {
            this.setState({ expandedPreviouslySeenNodeUuids: [] }, () => {
                if (this.state.orderActivityType) {
                    let tree = this.getTree(this.state.orderActivityType);
                    this.setTreeNodeSizes(tree);
                    let treeRenderDetails = this.getTreeRenderDetails(tree);
                    this.setState({ treeRenderDetails });
                }
            });
        };

        /**
         * Toggles the view type between "orderStatusReason" and "queue".
         */
        const handleToggleViewType = () => {
            this.setState(
                prevState => {
                    const newViewType = prevState.viewType === "orderStatusReason" ? "queue" : "orderStatusReason";
                    Setting.set("admin.queueRoutes.routingTree.viewMode", newViewType);
                    return { viewType: newViewType };
                },
                () => {
                    if (this.state.orderActivityType) {
                        let tree = this.getTree(this.state.orderActivityType);
                        this.setTreeNodeSizes(tree);
                        let treeRenderDetails = this.getTreeRenderDetails(tree);
                        this.setState({ treeRenderDetails });
                    }
                }
            );
        };

        return [
            <Menu
                menuItems={[
                    (
                        <MUIMenuItem
                            disabled={!this.state.orderActivityType}
                            onClick={handleDownload}
                        >
                            <MUIListItemIcon>
                                <MUIDownloadIcon fontSize="small" />
                            </MUIListItemIcon>
                            Download
                        </MUIMenuItem>
                    ),
                    (
                        <MUIMenuItem
                            disabled={this.state.expandedPreviouslySeenNodeUuids.length === 0}
                            onClick={handleResetView}
                        >
                            <MUIListItemIcon>
                                <MUIReplayIcon fontSize="small" />
                            </MUIListItemIcon>
                            Reset View
                        </MUIMenuItem>
                    ),
                    (
                        <MUIMenuItem
                            onClick={handleToggleViewType}
                        >
                            <MUIListItemIcon>
                                <MUISwapHorizIcon fontSize="small" />
                            </MUIListItemIcon>
                            {this.state.viewType === "orderStatusReason" ? "Show Queues" : "Show Reasons"}
                        </MUIMenuItem>
                    )
                ]}
            >
            </Menu>
        ];
    }
});